import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { get, post } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"

export const rankingError = code => ({
  SUPER_ADMIN_REQUIRED: "Your account no longer has permission to manage rankings.",
  RANKING_BUSY: "Another ranking operation is running. Wait for its result and review the current publication.",
  RANKING_SYNC_REQUIRED: "The published ranking still needs synchronization. Wait for recovery before starting another ranking operation.",
  RANKING_REVISION_CONFLICT: "The published ranking changed. Review the latest version before submitting again.",
  OPERATION_PAYLOAD_CONFLICT: "This request identifier belongs to a different intention. Check its original result.",
  RANKING_CALCULATION_INTERRUPTED: "The calculation was interrupted before publication. The previous ranking remains published.",
  RESTORE_ALREADY_PUBLISHED: "The previous-week ranking is already published. No restoration is needed.",
  RESTORE_TARGET_CHANGED: "The previous calendar week or its latest version changed. Review the restoration target again.",
  NO_VERIFIED_RESTORE_VERSION: "No complete, confirmed ranking is available for the previous calendar week.",
  INVALID_RANKING_REQUEST: "The ranking request was refused. Review the current ranking before submitting again; a restore also needs a reason.",
}[code] || "The requested ranking was not published. Review the current ranking before trying again.")

export default function useRankingOperation({ actorId, onChange }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const [state, setState] = useState({ status: "idle" })
  const active = useRef(null), changed = useRef(onChange)
  changed.current = onChange
  const key = `ranking-operation:${actorId}`
  const observe = useCallback((operation, submit = false, initial = false) => {
    active.current?.cancel()
    const kind = operation.kind || "recalculate"
    const watcher = { operation, ended: false, timer: null, attempts: 0 }
    active.current = watcher
    const current = () => active.current === watcher && !watcher.ended
    const cleanup = () => { clearTimeout(watcher.timer); socket?.off("leaderboards-response", onSocket) }
    const apply = receipt => {
      if (!current() || receipt?.operationId !== operation.operationId || !["pending", "calculating", "published", "completed", "failed"].includes(receipt.status)) return
      if ((receipt.kind || "recalculate") !== kind) return
      if (["published", "completed"].includes(receipt.status) && (!receipt.versionId || !receipt.publishedAt)) return
      if (receipt.status === "completed" && receipt.synchronization?.status !== "complete") return
      if (watcher.published && ["pending", "calculating", "failed"].includes(receipt.status)) return
      setState({ status: receipt.status, receipt, operation, message: receipt.status === "failed" ? rankingError(receipt.message) : null })
      if (["completed", "failed"].includes(receipt.status)) {
        watcher.ended = true; cleanup()
        try { sessionStorage.removeItem(key) } catch { /* The server receipt remains available. */ }
        changed.current?.()
      } else if (receipt.status === "published" && !watcher.published) {
        watcher.published = true; changed.current?.()
      }
    }
    const onSocket = response => {
      if (response?.operation !== (kind === "restore" ? "rankings-restored" : "rankings-recalculated") || response.operationId !== operation.operationId) return
      if (response.correlationId && response.correlationId !== operation.correlationId) return
      if (response.data) apply(response.data)
      // Only a durable receipt terminates polling; a transport-level error can
      // arrive after the actual write and does not establish its outcome.
    }
    const poll = async () => {
      if (!current()) return
      try { const response = await get(`/leaderboards/operations/${operation.operationId}`); apply(response?.data) }
      catch { /* Keep the stable request while its outcome is uncertain. */ }
      if (current()) watcher.timer = setTimeout(poll, 2000)
    }
    watcher.cancel = () => { watcher.ended = true; cleanup() }
    watcher.send = async () => {
      if (!current() || watcher.sending) return
      watcher.sending = true
      const firstSubmission = initial && ++watcher.attempts === 1
      try {
        await post(kind === "restore" ? "/leaderboards/restore" : "/leaderboards/generate", { ...operation.payload, operationId: operation.operationId, socketClientId },
          { headers: { "X-Correlation-Id": operation.correlationId } })
      } catch (error) {
        if (!current()) return
        if (firstSubmission && [400, 401, 403, 404, 422].includes(error.response?.status)) {
          apply({ kind, operationId: operation.operationId, status: "failed", message: [401, 403].includes(error.response.status) ? "SUPER_ADMIN_REQUIRED" : "INVALID_RANKING_REQUEST" })
        } else if (!watcher.published) setState({ status: "unknown", operation, message: "The request could not be confirmed. Its saved result is being checked; retrying will use the same request." })
      } finally { watcher.sending = false }
    }
    setState({ status: "pending", operation })
    socket?.on("leaderboards-response", onSocket)
    watcher.timer = setTimeout(poll, submit ? 2000 : 0)
    if (submit) void watcher.send()
  }, [key, socket, socketClientId])

  useEffect(() => {
    setState({ status: "idle" })
    if (actorId) try {
      const operation = JSON.parse(sessionStorage.getItem(key) || "null")
      if ([undefined, "recalculate", "restore"].includes(operation?.kind) && operation?.actorId === actorId && /^[a-zA-Z0-9-]{16,128}$/.test(operation.operationId || "")
        && operation.correlationId && typeof operation.payload?.reason === "string" && Number.isSafeInteger(operation.payload?.expectedRevision)) observe(operation)
    } catch { /* Invalid local data cannot initiate a command. */ }
    return () => { active.current?.cancel(); active.current = null }
  }, [key, actorId, observe])

  const start = (payload, kind = "recalculate") => {
    if (!actorId || (active.current && !active.current.ended)) return false
    let operation
    try {
      operation = { actorId, kind, operationId: newPlayerOperationId(), correlationId: newPlayerOperationId(), payload }
      sessionStorage.setItem(key, JSON.stringify(operation))
    }
    catch { setState({ status: "failed", message: "Session storage is unavailable. Enable it before submitting so the request can be recovered." }); return false }
    observe(operation, true, true)
    return true
  }
  return { ...state, start, pending: ["pending", "calculating", "unknown", "published"].includes(state.status),
    retry: () => active.current?.send?.() }
}
